import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { parseYoutubeId } from '@/lib/youtube';

type YouTubeHostStageProps = {
  /** True once the Welya session is live. */
  isLive: boolean;
  /** Public YouTube URL configured for the webinar. */
  youtubeUrl?: string | null;
};

export default function YouTubeHostStage({ isLive, youtubeUrl }: Readonly<YouTubeHostStageProps>) {
  const { t } = useTranslation('webinars');
  const videoId = useMemo(() => parseYoutubeId(youtubeUrl), [youtubeUrl]);
  const embedReady = Boolean(videoId);

  if (embedReady && videoId) {
    const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&iv_load_policy=3&playsinline=1&modestbranding=1&autoplay=1&mute=1&controls=1`;
    return (
      <div className="w-full h-full rounded-2xl overflow-hidden bg-black flex items-center justify-center">
        {/* controls=1 : les contrôles natifs YouTube sont actifs, dont la barre de progression du
            direct (indicateur LIVE), pour que le présentateur puisse suivre/rattraper le direct. */}
        <iframe
          src={embedUrl}
          title={t('live.youtube.host.title')}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full rounded-2xl bg-black/90 text-white flex flex-col items-center justify-center text-center gap-4 p-8">
      <div className="text-lg font-semibold">{t('live.youtube.host.title')}</div>
      <p className="max-w-md text-sm text-white/80">
        {isLive ? t('live.youtube.host.connecting') : t('live.youtube.host.prelive')}
      </p>
      {youtubeUrl && (
        <a
          href={youtubeUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-white/70 underline break-all"
        >
          {youtubeUrl}
        </a>
      )}
    </div>
  );
}
